/**
 * Super skills do time (docs/refactor/51, §6.2) — the organization's plugins. Write what you want and your own AI
 * writes it and publishes it under the name you gave. A team super skill (placement "superskills", Team plan) goes
 * into everybody's Super skills grid with its menu of options, nothing to install; a developer button is installed
 * by whoever wants it and appears where it belongs — the header, the Audit screen, or here.
 *
 * A plugin is buttons that send prompts, never code: installing one written by somebody else does
 * not let it reach your session, keys or files.
 */

import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Puzzle, Sparkles, Trash2 } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { atLeast, getSession } from '../lib/auth'
import { useActiveRepository } from '../lib/activeRepository'
import { Badge, EmptyState, ErrorBanner, Panel, Skeleton } from '../components/primitives'
import { PluginButtons } from '../components/PluginButtons'
import { PLUGIN_ICONS } from '../lib/plugins'
import type { PluginItem } from '../lib/types'

export default function Plugins() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const canWrite = atLeast(getSession()?.role, 'Developer')
  const { active } = useActiveRepository()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [sent, setSent] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const { data: plugins, isLoading } = useQuery({
    queryKey: ['plugins'],
    queryFn: api.plugins.list,
    // A plugin the AI is writing appears when it publishes it.
    refetchInterval: sent ? 5_000 : false,
  })
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['plugins'] })

  const create = useMutation({
    mutationFn: () => api.plugins.create({ name: name.trim(), description: description.trim(), repositoryId: active?.id ?? null }),
    onSuccess: () => {
      setSent(true)
      setProblem(null)
    },
    onError: (error) =>
      setProblem(error instanceof ApiError && error.code && ['remote_off', 'plan_limit', 'invalid_name', 'required', 'team_only'].includes(error.code) ? t(`plugins.error.${error.code}`) : t('error.generic')),
  })
  const install = useMutation({
    mutationFn: (plugin: PluginItem) => (plugin.installed ? api.plugins.uninstall(plugin.id) : api.plugins.install(plugin.id)),
    onSuccess: refresh,
  })
  const remove = useMutation({ mutationFn: (plugin: PluginItem) => api.plugins.remove(plugin.id), onSuccess: refresh })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!name.trim() || !description.trim()) return
    create.mutate()
  }

  return (
    <div className="stack">
      <p className="muted small" style={{ maxWidth: 760, margin: 0 }}>{t('plugins.intro')}</p>

      {canWrite && (
        <Panel title={t('plugins.createTitle')}>
          <form className="stack" onSubmit={submit}>
            <label className="field">
              <span className="field__label">{t('plugins.name')}</span>
              <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder={t('plugins.namePlaceholder')} />
            </label>
            <label className="field">
              <span className="field__label">{t('plugins.what')}</span>
              <textarea className="input" rows={4} value={description} maxLength={3000} onChange={(e) => setDescription(e.target.value)} placeholder={t('plugins.whatPlaceholder')} />
            </label>
            {problem && <ErrorBanner message={problem} />}
            {sent && (
              <div className="notice small">
                {t('plugins.creating')} <Link to="/agents/work">{t('run.follow')}</Link>
              </div>
            )}
            <div className="row row--end">
              <button type="submit" className="btn btn--primary" disabled={create.isPending || !name.trim() || !description.trim()}>
                <Sparkles size={14} /> {t('plugins.create')}
              </button>
            </div>
          </form>
        </Panel>
      )}

      <PluginButtons placement="plugins" />

      <h3 className="small" style={{ margin: 0 }}>{t('plugins.store')}</h3>
      {isLoading && <Skeleton height={120} />}
      {plugins && plugins.length === 0 && <EmptyState title={t('plugins.emptyTitle')} body={t('plugins.emptyBody')} />}
      <div className="repo-list">
        {(plugins ?? []).map((plugin) => (
          <div key={plugin.id} className="repo-row panel plugin-row">
            <Puzzle size={18} className="repo-row__icon" />
            <div className="stack" style={{ gap: 4, minWidth: 0, flex: 1 }}>
              <div className="row row--tight row--wrap">
                <strong>{plugin.name}</strong>
                <Badge tone="neutral">v{plugin.version}</Badge>
                {plugin.installed && <Badge tone="ok">{t('plugins.installed')}</Badge>}
                {plugin.authorName && <span className="tiny muted">{t('plugins.by', { who: plugin.authorName })}</span>}
              </div>
              <span className="small">{plugin.description}</span>
              <div className="row row--tight row--wrap">
                {plugin.commands.map((command) => {
                  const Icon = PLUGIN_ICONS[command.icon] ?? Puzzle
                  return (
                    <span key={command.id} className="tiny muted row row--tight" title={command.description}>
                      <Icon size={11} /> {command.title} · {t(`plugins.place.${command.placement}`)}
                    </span>
                  )
                })}
              </div>
            </div>
            {/* Only super skills: the whole team has them, nothing to install (51, §6.2). */}
            {plugin.commands.every((c) => c.placement === 'superskills') ? (
              <Badge tone="ok">{t('plugins.forEveryone')}</Badge>
            ) : canWrite && (
              <button type="button" className={`btn btn--sm ${plugin.installed ? 'btn--ghost' : 'btn--primary'}`} onClick={() => install.mutate(plugin)} disabled={install.isPending}>
                <Download size={13} /> {plugin.installed ? t('plugins.uninstall') : t('plugins.install')}
              </button>
            )}
            {plugin.mine && (
              <button type="button" className="btn btn--sm btn--ghost btn--icon" aria-label={t('plugins.remove')} title={t('plugins.remove')}
                onClick={() => window.confirm(t('plugins.removeConfirm', { name: plugin.name })) && remove.mutate(plugin)}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
